import { useState, useEffect, useRef } from 'react';
import styles from './TestConfig.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { writingAPI } from '../../services/apiServices';
import { useUser } from '../../context/UserContext';
import BackButton from '../common/BackButton/BackButton';

const TOPICS = [
  'Daily Life & Routines',
  'Travel & Adventure',
  'Technology & Innovation',
  'Environment & Nature',
  'Culture & Traditions',
  'Education & Learning',
  'Health & Wellness',
  'Food & Cuisine',
  'Sports & Recreation',
  'Arts & Entertainment'
];

const QUESTION_COUNTS = [5, 10, 15, 20];

const TestConfig = () => {
  const { userProgress } = useUser();
  const [selectedTopic, setSelectedTopic] = useState('');
  const [questionCount, setQuestionCount] = useState(5);
  const [error, setError] = useState('');
  const [isStartingTest, setIsStartingTest] = useState(false);
  const [creativeTopics, setCreativeTopics] = useState([]);
  const [persuasiveTopics, setPersuasiveTopics] = useState([]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [isTimed, setIsTimed] = useState(false);
  const [timeLimit, setTimeLimit] = useState(30); // 30 minutes default
  const location = useLocation();
  const modeTitle = location.state?.title;
  const isFetched = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isFetched.current) return;
    
    if (modeTitle === 'Creative Writing') {
      fetchCreativeTopics();
      isFetched.current = true;
    } else if (modeTitle === 'Persuasive Writing') {
      fetchPersuasiveTopics();
      isFetched.current = true;
    }
  }, [modeTitle]);

  const fetchCreativeTopics = async () => {
    setIsLoadingTopics(true);
    try {
      const grade = userProgress?.overallLevel || 4;
      const response = await writingAPI.getCreativeTopics(grade);
      setCreativeTopics(response.topics);
    } catch (error) {
      toast.error('Failed to load creative writing topics');
      console.error('Error fetching topics:', error);
    } finally {
      setIsLoadingTopics(false);
    }
  };

  const fetchPersuasiveTopics = async () => {
    setIsLoadingTopics(true);
    try {
      const grade = userProgress?.overallLevel || 4;
      const response = await writingAPI.getPersuasiveTopics(grade);
      setPersuasiveTopics(response.topics);
    } catch (error) {
      toast.error('Failed to load persuasive writing topics');
      console.error('Error fetching topics:', error);
    } finally {
      setIsLoadingTopics(false);
    }
  };

  const handleStartNewTest = async (testConfig) => {
    setIsStartingTest(true);
    try {
      let response;
      if (modeTitle === 'Creative Writing') {
        response = await writingAPI.startCreativeSession({
          topicId: testConfig?.topic,
          timed: isTimed,
          timeLimit: isTimed ? timeLimit : null,
          grade: userProgress?.overallLevel
        });
      } else if (modeTitle === 'Persuasive Writing') {
        response = await writingAPI.startPersuasiveSession({
          topicId: testConfig?.topic,
          timed: isTimed,
          timeLimit: isTimed ? timeLimit : null,
          grade: userProgress?.overallLevel
        });
      } else {
        response = await writingAPI.startNewSession(testConfig, userProgress?.overallLevel);
      }
      navigate(`/writing-test/${response.sessionId}`);
    } catch (error) {
      console.error('Failed to start test:', error);
      toast.error('Failed to start test. Please try again.');
    } finally {
      setIsStartingTest(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedTopic) {
      setError('Please select a topic');
      return;
    }

    handleStartNewTest({
      testType: modeTitle === 'Short Sentences' ? 'short_sentences' : 'short_story',
      topic: selectedTopic,
      questionCount: modeTitle === 'Short Sentences' ? questionCount : 1
    });

  };

  const handleSubmitWritings = (topic) => {
    if (!topic) {
      setError('Please select a topic');
      return;
    }
    
    setSelectedTopic(topic);

    if (modeTitle === 'Creative Writing') {
      handleStartNewTest({
        testType: 'creative_writing',
        topic: topic,
        timed: isTimed,
        timeLimit
      });

    } else {
      handleStartNewTest({
        testType: 'persuasive_writing',
        topic: topic,
        timed: isTimed,
        timeLimit
      });
    }
  };

  const renderTopicSelection = () => {
    if (modeTitle === 'Creative Writing') {
      if (isLoadingTopics) {
        return <div className={styles.loading}>Loading topics...</div>;
      }

      return (
        <div className={styles.topicsGrid}>
          {creativeTopics.map((topic, index) => (
            <div
              key={index}
              className={`${styles.topicCard} ${selectedTopic === topic.content ? styles.selected : ''}`}
              onClick={() => handleSubmitWritings(topic.content)}
            >
              <div className={styles.topicType}>{topic.type.split('_').join(' ')}</div>
              <div className={styles.topicContent}>{topic.content}</div>
              <div className={styles.topicGenre}>{topic.genre}</div>
            </div>
          ))}
        </div>
      );
    } else if (modeTitle === 'Persuasive Writing') {
      if (isLoadingTopics) {
        return <div className={styles.loading}>Loading topics...</div>;
      }

      return (
        <div className={styles.topicsGrid}>
          {persuasiveTopics.map((topic, index) => (
            <div
              key={index}
              className={`${styles.topicCard} ${selectedTopic === topic.content ? styles.selected : ''}`}
              onClick={() => handleSubmitWritings(topic.content)}
            >
              <div className={styles.topicType}>{topic.type}</div>
              <div className={styles.topicContent}>{topic.content}</div>
              {/* {topic.suggestedPoints && topic.suggestedPoints.length > 0 && (
                <div className={styles.topicHint}>
                  <span>Suggested points to consider</span>
                </div>
              )} */}
            </div>
          ))}
        </div>
      );
    }

    return (
      <select
        className={styles.select}
        value={selectedTopic}
        onChange={(e) => setSelectedTopic(e.target.value)}
      >
        <option value="" disabled>Choose a topic</option>
        {TOPICS.map((topic) => (
          <option key={topic} value={topic}>{topic}</option>
        ))}
      </select>
    );
  };


  return (
    <div className={`${styles.container} ${modeTitle === 'Persuasive Writing' || modeTitle === 'Creative Writing' ? styles.creativeTopics : ''}`}>
      <div className={styles.header}>
      </div>

      <div className={styles.content}>

    <div className={styles.headingContainer}>

      <h3 className={styles.sectionTitle}>
              {modeTitle === 'Creative Writing' 
                ? 'Select a Writing Prompt' 
                : modeTitle === 'Persuasive Writing'
                  ? 'Select a Persuasive Topic'
                  : 'Select a Topic'}
      </h3>

      {(modeTitle === 'Creative Writing' || modeTitle === 'Persuasive Writing') && (
          <div className={`${styles.section} ${styles.timerContainer}`}>
            <div className={styles.timerControl}>
            <span>Enable time limit</span>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={isTimed}
                  onChange={(e) => setIsTimed(e.target.checked)}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        )}
    </div>


        <div className={styles.section}>
          <div className={`${styles.formControl} ${!!error && !selectedTopic ? styles.error : ''}`}>
            {renderTopicSelection()}
            {error && !selectedTopic && (
              <p className={styles.errorText}>Please select a topic</p>
            )}
          </div>
        </div>


        {modeTitle === 'Short Sentences' && (
          <>
            <div className={styles.divider}></div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Number of Questions</h3>
              <div className={styles.formControl}>
                <select
                  className={styles.select}
                  value={questionCount}
                  onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                >
                  {QUESTION_COUNTS.map((count) => (
                    <option key={count} value={count}>{count} Questions</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {modeTitle === 'Short Story' && (
          <div className={styles.infoText}>
            <p>You will be presented with a short story (800-1000 words) and asked to rewrite it while correcting any mistakes.</p>
          </div>
        )}

        {/* {modeTitle === 'Persuasive Writing' && (
          <div className={styles.infoText}>
            <p>Write a persuasive piece focusing on argument structure, persuasive techniques, and grammar.</p>
          </div>
        )} */}

        {error && (
          <p className={styles.errorMessage}>{error}</p>
        )}
      </div>

      {
        (modeTitle === 'Short Sentences' || modeTitle === 'Short Story') && (
          <div className={styles.actions}>
            <button 
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={!selectedTopic || isStartingTest}
              >
                {isStartingTest ? 'Starting...' : 'Start Writing'}
              </button>
            </div> 
        )}

      <BackButton/>

    </div>
  );
};

export default TestConfig;